// Toma del pedido en el celular = COPIA LITERAL de la app de pedidos directos del B2B (madeci-b2b/
// ventas/index.html, pasos 2 y 3): ficha del cliente con su plata, «Suele comprar / Catálogo completo /
// En el pedido», filas con foto, Caja/Unidad y cantidad, barra del carro y «Revisar el pedido».
// Diferencia con el B2B: «Confirmar» deja el pedido en la cola del celular (con o sin señal) y el ERP
// decide precio, crédito y stock al llegar. El pedido a medio digitar se guarda solo, por cliente.
import { useEffect, useMemo, useRef, useState } from 'react';
import { coincide } from '../comun/coincide';
import { useSesion } from '../sesion/SesionContexto';
import {
  CLAVES,
  escribir,
  leer,
  nuevoIdExterno,
  type Borrador,
  type ClienteRuta,
  type CondicionYPlazo,
  type LineaBorrador,
  type Paquete,
  type PresentacionCat,
  type ProductoCat,
} from './almacen';
import { useCola } from './cola';
import { CuentaCorriente } from './Cuenta';
import {
  CONDICION,
  condicionTexto,
  esContado,
  pedidoBase,
  pesos,
  precioDe,
  presentacionInicial,
  rutConDv,
  totalAprox,
} from './logica';

/** Lo que se hizo con el cliente, para la pantalla de resultado. */
export type Terminado =
  | { tipo: 'PEDIDO'; lineas: number; total: number | null }
  | { tipo: 'VISITA' };

/** Fotos de producto del portal B2B (mismo código de producto que Random y el ERP). */
const FOTO = (sku: string) => `https://b2b.madeci.cl/img/productos/${encodeURIComponent(sku)}.webp`;

const vacio = (c: ClienteRuta): Borrador => {
  const despacho = c.direcciones.find((d) => d.despacho) ?? c.direcciones.find((d) => d.principal);
  return {
    lineas: [],
    documento:
      c.documentoPorDefecto === 'FACTURA' || c.documentoPorDefecto === 'BOLETA'
        ? c.documentoPorDefecto
        : null,
    entrega: 'DESPACHO',
    direccionId: despacho?.id ?? null,
    fechaEntrega: null,
    observaciones: '',
  };
};

const esCaja = (x: PresentacionCat) => Number(x.factor) > 1;
/** «$11.010 por caja · $1.835 c/u»: con caja siempre se ve el valor de la unidad (Rodrigo 27-10). */
const porUnidad = (precio: number | null, x: PresentacionCat | undefined) =>
  precio !== null && x && esCaja(x) ? ` · ${pesos(precio / Number(x.factor))} c/u` : '';
const mismaCondicion = (a: CondicionYPlazo, b: CondicionYPlazo) =>
  a.condicion === b.condicion && Number(a.plazoDias) === Number(b.plazoDias);
const DOCUMENTO: Record<string, string> = { FACTURA: 'Factura', BOLETA: 'Boleta' };
const fecha = (iso: string) => `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(2, 4)}`;
const isoDia = (d: Date) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

export function TomarPedido({
  paquete,
  cliente: c,
  alCambiarCliente,
  alTerminar,
}: {
  paquete: Paquete;
  cliente: ClienteRuta;
  alCambiarCliente: () => void;
  alTerminar: (hecho: Terminado) => void;
}) {
  const cat = paquete.catalogo;
  const cola = useCola();
  const s = useSesion();
  // Los mismos permisos de la nota del escritorio (pedido de Rodrigo 27-10); la nota los vuelve a exigir.
  const puede = {
    precio: s.puede('ventas.precio.manual'),
    lista: s.puede('ventas.nota.cambiar_lista_precio'),
    condicion: s.puede('ventas.nota.cambiar_condicion_pago'),
    vendedor: s.puede('ventas.nota.cambiar_vendedor'),
    autorizar: s.puede('ventas.nota.aprobar'),
  };
  const claveBorrador = CLAVES.borrador(paquete.empresaId, c.entidadId);
  const [b, setB] = useState<Borrador>(() => vacio(c));
  const listaId = b.listaPrecioId ?? c.listaPrecioId;
  const lista = cat.listas.find((l) => l.id === listaId);
  // La del cliente y las alternativas del canal (solo las que el celular tiene con precios).
  const listasPermitidas = cat.listas.filter(
    (l) => l.id === c.listaPrecioId || (cat.listasAlternativas ?? []).includes(l.id),
  );
  const condicionCliente: CondicionYPlazo = {
    condicion: c.condicionPago,
    plazoDias: esContado(c) ? 0 : c.plazoDias,
  };
  const condicion = b.condicion ?? condicionCliente;
  const opcionesPago = [
    condicionCliente,
    ...(c.opcionesPago ?? []).filter((o) => !mismaCondicion(o, condicionCliente)),
  ];
  const vendedores = paquete.contexto.vendedores ?? [];
  const vendedorNota =
    b.vendedorNotaId ?? c.vendedorId ?? (c.vendedor ? null : paquete.contexto.vendedor.id);
  const vendedorTexto = (() => {
    const v = vendedores.find((x) => x.id === vendedorNota);
    if (v) return `${v.codigo} · ${v.nombre}`;
    if (c.vendedor) return c.vendedor;
    return `${paquete.contexto.vendedor.codigo} · ${paquete.contexto.vendedor.nombre}`;
  })();
  const autorizar = puede.autorizar && b.autorizar !== false;
  const [cargado, setCargado] = useState(false);
  const [paso, setPaso] = useState<'pedido' | 'checkout'>('pedido');
  const [tab, setTab] = useState<'habituales' | 'catalogo' | 'carro'>(
    c.habituales.length ? 'habituales' : 'catalogo',
  );
  const [texto, setTexto] = useState('');
  const [grabando, setGrabando] = useState(false);
  const [falta, setFalta] = useState<string | null>(null);
  const [elegida, setElegida] = useState<Record<string, string>>({});

  useEffect(() => {
    void leer<Borrador>(claveBorrador).then((x) => {
      if (x?.lineas.length) setB(x);
      setCargado(true);
    });
  }, [claveBorrador]);
  const primero = useRef(true);
  useEffect(() => {
    if (!cargado) return;
    if (primero.current) {
      primero.current = false;
      return;
    }
    void escribir(claveBorrador, b.lineas.length ? b : null);
  }, [b, cargado, claveBorrador]);
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* jsdom */
    }
  }, [paso]);

  const productos = useMemo(() => new Map(cat.productos.map((p) => [p.id, p])), [cat.productos]);
  const habitual = new Map(c.habituales.map((h) => [h.productoId, h]));
  const habituales = c.habituales
    .map((h) => productos.get(h.productoId))
    .filter(Boolean) as ProductoCat[];
  const enCarro = [...new Set(b.lineas.map((l) => l.productoId))]
    .map((id) => productos.get(id))
    .filter(Boolean) as ProductoCat[];
  const q = texto.trim();
  // Como el B2B: habituales se filtran; el catálogo completo pide texto (son miles de productos).
  const mostrar: ProductoCat[] | null =
    tab === 'carro'
      ? enCarro
      : tab === 'habituales'
        ? habituales.filter((p) => !q || coincide(q, p.nombre, p.sku))
        : q
          ? cat.productos.filter((p) => coincide(q, p.nombre, p.sku)).slice(0, 80)
          : null;

  const lineaDe = (productoId: string, presentacionId: string) =>
    b.lineas.find((l) => l.productoId === productoId && l.presentacionId === presentacionId);
  const fijar = (productoId: string, presentacionId: string, cantidad: string) =>
    setB((x) => {
      const resto = x.lineas.filter(
        (l) => !(l.productoId === productoId && l.presentacionId === presentacionId),
      );
      const n = Math.max(0, Math.floor(Number(cantidad.replace(',', '.')) || 0));
      const antes = x.lineas.find(
        (l) => l.productoId === productoId && l.presentacionId === presentacionId,
      );
      // Cambiar la cantidad conserva el precio puesto a mano y el lugar de la línea.
      const nueva = {
        productoId,
        presentacionId,
        cantidad: String(n),
        ...(antes?.precioManual ? { precioManual: antes.precioManual } : {}),
      };
      return {
        ...x,
        lineas: !n ? resto : antes ? x.lineas.map((l) => (l === antes ? nueva : l)) : [...resto, nueva],
      };
    });
  /** Cambiar de caja a unidad (o al revés) mueve la línea completa, como el B2B. */
  const cambiarPresentacion = (p: ProductoCat, de: PresentacionCat, a: PresentacionCat) => {
    if (de.id === a.id) return;
    setElegida((x) => ({ ...x, [p.id]: a.id }));
    const l = lineaDe(p.id, de.id);
    if (l)
      setB((x) => ({
        ...x,
        lineas: [
          ...x.lineas.filter((y) => !(y.productoId === p.id && y.presentacionId === de.id)),
          { productoId: p.id, presentacionId: a.id, cantidad: l.cantidad },
        ],
      }));
  };

  const factorDe = (l: LineaBorrador) =>
    Number(
      productos.get(l.productoId)?.presentaciones.find((x) => x.id === l.presentacionId)?.factor ??
        1,
    );
  const total = totalAprox(cat, listaId, b.lineas);
  const unidades = b.lineas.reduce((s, l) => s + Number(l.cantidad) * factorDe(l), 0);

  const fijarPrecio = (productoId: string, presentacionId: string, valor: string) =>
    setB((x) => ({
      ...x,
      lineas: x.lineas.map((l) => {
        if (l.productoId !== productoId || l.presentacionId !== presentacionId) return l;
        const n = Math.round(Number(valor.replace(/\D/g, '')) || 0);
        const resto = { productoId, presentacionId, cantidad: l.cantidad };
        const deLista = precioDe(cat, listaId, presentacionId, Number(l.cantidad));
        return n > 0 && n !== deLista ? { ...resto, precioManual: String(n) } : resto;
      }),
    }));

  const grabar = async () => {
    if (b.entrega === 'DESPACHO' && !b.direccionId && c.direcciones.length)
      return setFalta('Elige la dirección de despacho.');
    setGrabando(true);
    const id = nuevoIdExterno();
    const lineas: LineaBorrador[] = b.lineas.map((l) =>
      puede.precio || !l.precioManual
        ? l
        : { productoId: l.productoId, presentacionId: l.presentacionId, cantidad: l.cantidad },
    );
    const cambiaLista = !!b.listaPrecioId && b.listaPrecioId !== c.listaPrecioId;
    const cambiaCondicion = !!b.condicion && !mismaCondicion(b.condicion, condicionCliente);
    const cambiaVendedor = puede.vendedor && !!vendedorNota && vendedorNota !== c.vendedorId;
    await cola.agregar({
      id,
      tipo: 'PEDIDO',
      empresaId: paquete.empresaId,
      clave: `ped-${id}`,
      cuerpo: {
        empresaId: paquete.empresaId,
        ...(paquete.verComo ? { vendedorId: paquete.verComo } : {}),
        idExterno: id,
        clienteEntidadId: c.entidadId,
        entrega: b.entrega,
        ...(cambiaLista ? { listaPrecioId: b.listaPrecioId } : {}),
        ...(cambiaCondicion
          ? { condicionPago: b.condicion!.condicion, plazoDias: Number(b.condicion!.plazoDias) }
          : {}),
        ...(cambiaVendedor ? { vendedorNotaId: vendedorNota } : {}),
        ...(autorizar ? { autorizar: true } : {}),
        ...(b.entrega === 'DESPACHO' && b.direccionId ? { direccionEntregaId: b.direccionId } : {}),
        ...(b.fechaEntrega ? { fechaEntrega: b.fechaEntrega } : {}),
        observaciones: b.observaciones.trim() || null,
        lineas,
        tomadoEn: new Date().toISOString(),
      },
      cliente: c.razonSocial,
      totalAprox: total,
      lineas: lineas.length,
      creadoEn: new Date().toISOString(),
    });
    await escribir(claveBorrador, null);
    setGrabando(false);
    alTerminar({ tipo: 'PEDIDO', lineas: lineas.length, total });
  };

  // ============================================================ PASO 3 — revisar y confirmar
  if (paso === 'checkout') {
    const hoy = new Date();
    const faltantes = b.lineas.filter(
      (l) => pedidoBase(cat, b.lineas, l.productoId) > Number(cat.stock[l.productoId] ?? 0),
    );
    return (
      <div className="wrap">
        <Pasos paso={3} />
        <h2 className="tit">Revisar el pedido</h2>
        <p className="sub">
          {c.razonSocial} · RUT {rutConDv(c)}
        </p>

        <div className="caja">
          <div className="caja-tit">
            <h3>Productos</h3>
            <span className="chip gris">{b.lineas.length}</span>
          </div>
          <p className="h-sub">
            Toca la cantidad para corregirla
            {puede.precio ? '; el precio (con IVA) también se puede cambiar' : ''}.
          </p>
          {b.lineas.map((l) => {
            const p = productos.get(l.productoId);
            const pres = p?.presentaciones.find((x) => x.id === l.presentacionId);
            const deLista = precioDe(cat, listaId, l.presentacionId, Number(l.cantidad));
            const precio = l.precioManual ? Number(l.precioManual) : deLista;
            const un = Number(l.cantidad) * factorDe(l);
            const hay = Math.max(0, Math.round(Number(cat.stock[l.productoId] ?? 0)));
            const por = pres && esCaja(pres) ? 'caja' : 'unidad';
            return (
              <div key={`${l.productoId}|${l.presentacionId}`} className="linea">
                <div className="cuerpo">
                  <div className="nom">{p?.nombre ?? 'Producto'}</div>
                  <div className="meta">
                    Cód. {p?.sku} · {precio === null ? 'sin precio' : pesos(precio)} por {por}
                    {porUnidad(precio, pres)} · {un} unidades
                    {pedidoBase(cat, b.lineas, l.productoId) > hay && (
                      <>
                        {' · '}
                        <b style={{ color: 'var(--rojo)' }}>solo hay {hay}</b>
                      </>
                    )}
                  </div>
                  {puede.precio && (
                    <div className="precio-manual">
                      <PrecioUnitario
                        id={`pm-${l.productoId}-${l.presentacionId}`}
                        factor={Number(pres?.factor ?? 1)}
                        precio={precio}
                        alCambiar={(v) => fijarPrecio(l.productoId, l.presentacionId, v)}
                      />
                      {l.precioManual && (
                        <>
                          <span className="chip ambar">manual</span>
                          <button
                            type="button"
                            className="link"
                            onClick={() => fijarPrecio(l.productoId, l.presentacionId, '')}
                          >
                            volver a lista {deLista === null ? '' : pesos(deLista)}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <Cantidad
                  nombre={p?.nombre ?? ''}
                  valor={l.cantidad}
                  alCambiar={(v) => fijar(l.productoId, l.presentacionId, v)}
                />
                <div className="sub">
                  {precio === null ? '—' : pesos(precio * Number(l.cantidad))}
                </div>
                <button
                  className="quitar"
                  type="button"
                  title="Quitar del pedido"
                  aria-label={`Quitar ${p?.nombre ?? ''}`}
                  onClick={() => fijar(l.productoId, l.presentacionId, '0')}
                >
                  ×
                </button>
              </div>
            );
          })}
          <div className="totales grande">
            <span>Total{lista?.semantica === 'neto' ? ' (neto)' : ''}</span>
            <span>{pesos(total)}</span>
          </div>
          {faltantes.length > 0 && (
            <div className="aviso" style={{ marginTop: 12 }}>
              ⚠ Hay {faltantes.length} producto(s) con menos stock del pedido. Si al llegar no
              alcanza, entra lo disponible y te avisa.
            </div>
          )}
        </div>

        <div className="caja">
          <div className="caja-tit">
            <h3>Condiciones</h3>
          </div>
          <p className="h-sub">Las de la ficha del cliente{puede.condicion || puede.vendedor ? '; puedes cambiarlas' : ''}.</p>
          <div className="filas">
            <div className="fila">
              <span className="l">Documento</span>
              <span className="v">
                {DOCUMENTO[c.documentoPorDefecto ?? ''] ?? 'Factura'}
                <small> según la ficha</small>
              </span>
            </div>
            <div className="fila">
              <label className="l" htmlFor="ck-lista">
                Lista de precios
              </label>
              {puede.lista && listasPermitidas.length > 1 ? (
                <select
                  id="ck-lista"
                  value={listaId ?? ''}
                  onChange={(e) =>
                    setB({
                      ...b,
                      listaPrecioId: e.target.value === c.listaPrecioId ? null : e.target.value,
                    })
                  }
                >
                  {listasPermitidas.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.codigo} · {x.nombre}
                      {x.id === c.listaPrecioId ? ' (del cliente)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="v">{lista ? `${lista.codigo} · ${lista.nombre}` : 'Sin lista'}</span>
              )}
            </div>
            <div className="fila">
              <label className="l" htmlFor="ck-cond">
                Condición de pago
              </label>
              {puede.condicion && opcionesPago.length > 1 ? (
                <select
                  id="ck-cond"
                  value={`${condicion.condicion}|${condicion.plazoDias}`}
                  onChange={(e) => {
                    const o = opcionesPago.find(
                      (x) => `${x.condicion}|${x.plazoDias}` === e.target.value,
                    )!;
                    setB({ ...b, condicion: mismaCondicion(o, condicionCliente) ? null : o });
                  }}
                >
                  {opcionesPago.map((o) => (
                    <option key={`${o.condicion}|${o.plazoDias}`} value={`${o.condicion}|${o.plazoDias}`}>
                      {CONDICION[o.condicion] ?? o.condicion}
                      {o.plazoDias ? ` ${o.plazoDias} días` : ''}
                      {mismaCondicion(o, condicionCliente) ? ' (del cliente)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="v">{condicionTexto(condicion)}</span>
              )}
            </div>
            <div className="fila">
              <label className="l" htmlFor="ck-vend">
                Vendedor
              </label>
              {puede.vendedor && vendedores.length > 0 ? (
                <select
                  id="ck-vend"
                  value={vendedorNota ?? ''}
                  onChange={(e) => setB({ ...b, vendedorNotaId: e.target.value || null })}
                >
                  {!vendedorNota && <option value="">Elige…</option>}
                  {vendedores.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.codigo} · {v.nombre}
                      {v.id === c.vendedorId ? ' (del cliente)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="v">{vendedorTexto}</span>
              )}
            </div>
          </div>
          {puede.autorizar && (
            <label className="check">
              <input
                type="checkbox"
                checked={autorizar}
                onChange={(e) => setB({ ...b, autorizar: e.target.checked })}
              />
              <span>
                <b>Autorizarlo yo</b>
                <small>Si el crédito, la lista o el precio piden firma, la firmo al enviarlo.</small>
              </span>
            </label>
          )}
        </div>

        <div className="caja">
          <h3>Entrega</h3>
          <p className="h-sub">A dónde va y cuándo.</p>
          <div className="opciones" role="radiogroup" aria-label="Entrega">
            {(
              [
                ['DESPACHO', 'Despacho a domicilio', 'Se lo llevamos'],
                ['RETIRO', 'Retiro en local', 'Sucursal Cartagena · Av. Cartagena 498'],
              ] as const
            ).map(([v, t, sub]) => (
              <label key={v} className={`op${b.entrega === v ? ' on' : ''}`}>
                <input
                  type="radio"
                  name="ent"
                  value={v}
                  checked={b.entrega === v}
                  onChange={() => setB({ ...b, entrega: v })}
                />
                <div>
                  <div className="t">{t}</div>
                  <div className="s">{sub}</div>
                </div>
              </label>
            ))}
          </div>
          {b.entrega === 'DESPACHO' && c.direcciones.length > 0 && (
            <>
              <label className="et" htmlFor="ck-dir">
                Dirección de despacho
              </label>
              <select
                id="ck-dir"
                value={b.direccionId ?? ''}
                onChange={(e) => setB({ ...b, direccionId: e.target.value || null })}
              >
                {!b.direccionId && <option value="">Elige…</option>}
                {c.direcciones.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.texto || 'Sin dirección'}
                    {d.comuna ? `, ${d.comuna}` : ''}
                    {d.principal ? ' (principal)' : d.despacho ? ' (despacho)' : ''}
                  </option>
                ))}
              </select>
            </>
          )}
          <label className="et" htmlFor="ck-fecha">
            Fecha (opcional)
          </label>
          <input
            id="ck-fecha"
            type="date"
            min={isoDia(hoy)}
            max={isoDia(new Date(hoy.getTime() + 14 * 86400000))}
            value={b.fechaEntrega ?? ''}
            onChange={(e) => setB({ ...b, fechaEntrega: e.target.value || null })}
          />
          <label className="et" htmlFor="ck-obs">
            Observaciones para el equipo (opcional)
          </label>
          <textarea
            id="ck-obs"
            rows={2}
            maxLength={450}
            placeholder="Ej: llamar antes de llegar, dejar en bodega…"
            value={b.observaciones}
            onChange={(e) => setB({ ...b, observaciones: e.target.value })}
          />
        </div>

        {falta && <div className="error">{falta}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          <button className="btn sec" type="button" onClick={() => setPaso('pedido')}>
            Seguir cargando
          </button>
          <button
            className="btn verde"
            style={{ flex: 1 }}
            type="button"
            disabled={!b.lineas.length || grabando}
            onClick={() => void grabar()}
          >
            {grabando
              ? 'Grabando…'
              : b.lineas.length
                ? 'Confirmar pedido'
                : 'Agrega al menos un producto'}
          </button>
        </div>
        <p className="sub" style={{ marginTop: 10, textAlign: 'center' }}>
          Entra como Nota de Venta del vendedor <b>{vendedorTexto}</b>.{' '}
          {autorizar
            ? 'Si pide firma, queda autorizada a tu nombre.'
            : 'Si el crédito o el precio necesitan firma, queda esperando la autorización de la oficina.'}
        </p>
      </div>
    );
  }

  // ============================================================ PASO 2 — cargar productos
  return (
    <>
      <div className="wrap">
        <Pasos paso={2} />
        <FichaCliente
          paquete={paquete}
          c={c}
          lista={lista}
          listas={puede.lista ? listasPermitidas : []}
          cambiarLista={(id) =>
            setB((x) => ({ ...x, listaPrecioId: id === c.listaPrecioId ? null : id }))
          }
          cambiar={() => {
            if (
              b.lineas.length &&
              !window.confirm(
                'Tienes productos cargados en este pedido (quedan guardados). ¿Elegir otro cliente?',
              )
            )
              return;
            alCambiarCliente();
          }}
          alNoCompro={() => alTerminar({ tipo: 'VISITA' })}
        />
        <div className="tabs">
          <button
            className={`tab${tab === 'habituales' ? ' on' : ''}`}
            type="button"
            onClick={() => setTab('habituales')}
          >
            Suele comprar
          </button>
          <button
            className={`tab${tab === 'catalogo' ? ' on' : ''}`}
            type="button"
            onClick={() => setTab('catalogo')}
          >
            Catálogo completo
          </button>
          <button
            className={`tab${tab === 'carro' ? ' on' : ''}`}
            type="button"
            onClick={() => setTab('carro')}
          >
            En el pedido ({b.lineas.length})
          </button>
        </div>
        {tab !== 'carro' && (
          <div className="buscador">
            <input
              type="search"
              placeholder="Buscar producto por nombre o código…"
              aria-label="Buscar producto por nombre o código"
              autoComplete="off"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
            <button
              className="limpiar"
              type="button"
              aria-label="Limpiar"
              onClick={() => setTexto('')}
            >
              ×
            </button>
          </div>
        )}
        {!listaId && (
          <div className="aviso" style={{ marginBottom: 12 }}>
            ℹ El cliente no tiene lista de precios: la oficina debe asignarla.
          </div>
        )}
        {mostrar === null ? (
          <div className="vacio">
            Escribe el nombre, la marca o el código del producto para buscarlo en el catálogo.
          </div>
        ) : !mostrar.length ? (
          <div className="vacio">
            {tab === 'carro' ? (
              'El pedido está vacío. Carga productos desde “Suele comprar” o el catálogo.'
            ) : tab === 'habituales' && !q ? (
              <>
                Este cliente no tiene compras recientes.
                <br />
                Usa el catálogo completo.
              </>
            ) : (
              'Ningún producto calza con esa búsqueda.'
            )}
          </div>
        ) : (
          <div>
            {mostrar.map((p) => {
              // Unidad en pantalla: la elegida a mano, si no la que ya está en el pedido, si no la caja.
              const cargada = p.presentaciones.find((x) => lineaDe(p.id, x.id));
              const pres =
                p.presentaciones.find((x) => x.id === elegida[p.id]) ??
                cargada ??
                presentacionInicial(p, cat, listaId);
              const linea = lineaDe(p.id, pres.id);
              const cant = linea?.cantidad ?? '';
              const precio = precioDe(cat, listaId, pres.id, Math.max(Number(cant || 0), 1));
              const precioBase = precioDe(cat, listaId, pres.id, 1);
              const disponible = Math.max(0, Math.round(Number(cat.stock[p.id] ?? 0)));
              const caja = p.presentaciones.find(esCaja);
              const unidad = p.presentaciones.find((x) => !esCaja(x));
              const h = habitual.get(p.id);
              const conEscalas = Object.keys(cat.escalas[listaId ?? '']?.[pres.id] ?? {}).length > 0;
              return (
                <div key={p.id} className={`prod${linea ? ' enCarro' : ''}`}>
                  <Foto sku={p.sku} />
                  <div className="cuerpo">
                    <div className="nom">{p.nombre}</div>
                    <div className="meta">
                      Cód. {p.sku}
                      {caja ? ` · caja de ${Number(caja.factor)}` : ''} · stock {disponible} un.
                    </div>
                    {(disponible <= 0 || conEscalas || h) && (
                      <div style={{ marginTop: 5 }}>
                        {disponible <= 0 && <span className="chip rojo">Sin stock</span>}
                        {conEscalas && <span className="chip verde">Precio por volumen</span>}
                        {h && (
                          <span className="chip indigo">
                            {h.veces}× · última {fecha(h.ultima)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {precioBase === null ? (
                    <div
                      className="precio"
                      style={{
                        color: 'var(--rojo)',
                        fontSize: 12.5,
                        maxWidth: 150,
                        textAlign: 'right',
                      }}
                    >
                      Sin precio en la lista {lista?.codigo ?? ''}
                    </div>
                  ) : (
                    <>
                      <div className="precio">
                        {pesos(precio)}{' '}
                        <small>
                          {esCaja(pres) ? 'por caja' : 'por unidad'}
                          {porUnidad(precio, pres)}
                          {precio !== null && precio < precioBase
                            ? ` · antes ${pesos(precioBase)}`
                            : ''}
                        </small>
                      </div>
                      <div className="acc">
                        {caja && unidad && p.presentaciones.length === 2 ? (
                          <div className="uds" role="group" aria-label={`Presentación ${p.nombre}`}>
                            {[caja, unidad].map((x) => (
                              <button
                                key={x.id}
                                type="button"
                                className={x.id === pres.id ? 'on' : ''}
                                aria-pressed={x.id === pres.id}
                                disabled={precioDe(cat, listaId, x.id) === null}
                                title={
                                  precioDe(cat, listaId, x.id) === null
                                    ? 'Sin precio en la lista para esta unidad'
                                    : undefined
                                }
                                onClick={() => cambiarPresentacion(p, pres, x)}
                              >
                                {esCaja(x) ? 'Caja' : 'Unidad'}
                              </button>
                            ))}
                          </div>
                        ) : p.presentaciones.length > 2 ? (
                          <select
                            aria-label={`Presentación ${p.nombre}`}
                            style={{ width: 'auto', padding: '7px 8px', fontSize: 13 }}
                            value={pres.id}
                            onChange={(e) =>
                              cambiarPresentacion(
                                p,
                                pres,
                                p.presentaciones.find((x) => x.id === e.target.value)!,
                              )
                            }
                          >
                            {p.presentaciones.map((x) => (
                              <option key={x.id} value={x.id}>
                                {x.nombre}
                                {esCaja(x) ? ` ×${Number(x.factor)}` : ''}
                              </option>
                            ))}
                          </select>
                        ) : null}
                        <Cantidad
                          nombre={p.nombre}
                          valor={cant}
                          alCambiar={(v) => fijar(p.id, pres.id, v)}
                        />
                      </div>
                      {puede.precio && linea && (
                        <div className="precio-manual en-prod">
                          <PrecioUnitario
                            id={`pp-${p.id}-${pres.id}`}
                            factor={Number(pres.factor)}
                            precio={linea.precioManual ? Number(linea.precioManual) : precio}
                            alCambiar={(v) => fijarPrecio(p.id, pres.id, v)}
                          />
                          {linea.precioManual && <span className="chip ambar">manual</span>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {b.lineas.length > 0 && (
        <div className="barra-carro">
          <div className="info">
            <div className="t">{pesos(total)}</div>
            <div className="s">
              {b.lineas.length} producto(s) · {unidades} unidades
            </div>
          </div>
          <button className="btn verde" type="button" onClick={() => setPaso('checkout')}>
            Revisar pedido
          </button>
        </div>
      )}
    </>
  );
}

// ============================================================ ficha del cliente (como el B2B)

function FichaCliente({
  paquete,
  c,
  lista,
  listas,
  cambiarLista,
  cambiar,
  alNoCompro,
}: {
  paquete: Paquete;
  c: ClienteRuta;
  lista: Paquete['catalogo']['listas'][number] | undefined;
  /** Listas a elegir (vacío = no puede cambiarla). */
  listas: Paquete['catalogo']['listas'];
  cambiarLista: (id: string) => void;
  cambiar: () => void;
  alNoCompro: () => void;
}) {
  const cola = useCola();
  const [sinVenta, setSinVenta] = useState(false);
  const [comentario, setComentario] = useState('');
  const linea = c.lineaCredito === null ? null : Number(c.lineaCredito);
  const deuda = c.deuda ? Number(c.deuda.deuda) : null;
  const vencidos = c.deuda ? Number(c.deuda.vencidos) : 0;
  const cheques = c.deuda ? Number(c.deuda.chequesEnCartera) : 0;
  const d = c.direcciones.find((x) => x.principal) ?? c.direcciones[0];
  const aCredito = !esContado(c);
  const disponible = linea !== null && deuda !== null ? linea - deuda : null;

  const registrarSinVenta = async () => {
    const id = nuevoIdExterno();
    await cola.agregar({
      id,
      tipo: 'VISITA',
      empresaId: paquete.empresaId,
      clave: `vis-${id}`,
      cuerpo: {
        empresaId: paquete.empresaId,
        ...(paquete.verComo ? { vendedorId: paquete.verComo } : {}),
        idExterno: id,
        clienteEntidadId: c.entidadId,
        comentario: comentario.trim() || null,
        tomadoEn: new Date().toISOString(),
      },
      cliente: c.razonSocial,
      totalAprox: null,
      lineas: 0,
      creadoEn: new Date().toISOString(),
    });
    alNoCompro();
  };

  const alerta: string[] = [];
  if (c.bloqueado)
    alerta.push(
      `Este cliente está BLOQUEADO${c.motivoBloqueo ? ` (${c.motivoBloqueo})` : ''}: el pedido entra y espera la firma de la oficina.`,
    );
  if (vencidos > 0)
    alerta.push(
      `Tiene ${pesos(vencidos)} VENCIDO. El pedido entra y puede quedar esperando la autorización.`,
    );

  return (
    <div className="ficha">
      <div className="fila1">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="nom">{c.razonSocial}</div>
          <div className="rut">
            RUT {rutConDv(c)}
            {c.nombreFantasia ? ` · ${c.nombreFantasia}` : ''}
          </div>
          <div className="dir">
            {d?.texto || 'Sin dirección en la ficha'}
            {d?.comuna ? `, ${d.comuna}` : ''}
            {c.telefono ? ` · ☎ ${c.telefono}` : ''}
          </div>
          <div style={{ marginTop: 9 }}>
            {listas.length < 2 && (
              <span className="chip gris">Lista {lista?.codigo ?? 'por defecto'}</span>
            )}
            <span className={`chip ${aCredito ? 'indigo' : 'verde'}`}>{condicionTexto(c)}</span>
            {c.bloqueado && <span className="chip rojo">BLOQUEADO</span>}
          </div>
        </div>
        <button className="btn sec sm" type="button" onClick={cambiar}>
          Cambiar cliente
        </button>
      </div>
      {listas.length > 1 && (
        <div className="lista-precio">
          <label htmlFor="f-lista">Lista de precios</label>
          <select
            id="f-lista"
            value={lista?.id ?? ''}
            onChange={(e) => cambiarLista(e.target.value)}
          >
            {listas.map((x) => (
              <option key={x.id} value={x.id}>
                {x.codigo} · {x.nombre}
                {x.id === c.listaPrecioId ? ' (del cliente)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <h3 className="oculto-visual">Crédito y deuda</h3>
      <div className="plata">
        <div className="b">
          <div className="l">Deuda pendiente</div>
          <div className="n">{deuda === null ? 'Sin dato' : pesos(deuda)}</div>
        </div>
        {vencidos > 0 && (
          <div className="b malo">
            <div className="l">Vencido</div>
            <div className="n">{pesos(vencidos)}</div>
          </div>
        )}
        {aCredito && linea !== null && linea > 0 && (
          <>
            <div className="b">
              <div className="l">Línea de crédito</div>
              <div className="n">{pesos(linea)}</div>
            </div>
            {disponible !== null && (
              <div className={`b ${disponible > 0 ? 'bueno' : 'malo'}`}>
                <div className="l">Cupo disponible</div>
                <div className="n">{pesos(Math.max(0, disponible))}</div>
              </div>
            )}
          </>
        )}
        {cheques > 0 && (
          <div className="b">
            <div className="l">Cheques en cartera</div>
            <div className="n">{pesos(cheques)}</div>
          </div>
        )}
      </div>
      {alerta.length > 0 && (
        <div className="aviso" style={{ marginTop: 12 }}>
          ⚠{' '}
          {alerta.map((a, i) => (
            <span key={i}>
              {i > 0 && <br />}
              {a}
            </span>
          ))}
        </div>
      )}
      <CuentaCorriente paquete={paquete} cliente={c} />
      <div className="ficha-acc">
        {c.telefono && (
          <a className="btn sec sm" href={`tel:${c.telefono.replace(/\s/g, '')}`}>
            ☎ Llamar
          </a>
        )}
        {d?.texto && (
          <a
            className="btn sec sm"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              [d.texto, d.comuna].filter(Boolean).join(', '),
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            Mapa
          </a>
        )}
        <button
          className="btn sec sm"
          type="button"
          aria-expanded={sinVenta}
          onClick={() => setSinVenta((x) => !x)}
        >
          No compró
        </button>
      </div>
      {sinVenta && (
        <div className="sinventa">
          <label className="et" htmlFor="sv-comentario">
            Comentario (opcional)
          </label>
          <textarea
            id="sv-comentario"
            rows={2}
            maxLength={500}
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Ej.: tiene stock, volver el jueves"
          />
          <button
            className="btn block"
            style={{ marginTop: 10 }}
            type="button"
            onClick={() => void registrarSinVenta()}
          >
            Registrar visita
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================ piezas

/** Dónde va el pedido: 1 Cliente › 2 Productos › 3 Revisar (estructura pedida por Rodrigo, 27-10). */
export function Pasos({ paso }: { paso: 1 | 2 | 3 }) {
  return (
    <ol className="pasos" aria-label="Pasos del pedido">
      {['Cliente', 'Productos', 'Revisar'].map((t, i) => (
        <li
          key={t}
          className={i + 1 === paso ? 'on' : i + 1 < paso ? 'hecho' : ''}
          aria-current={i + 1 === paso ? 'step' : undefined}
        >
          <span className="n">{i + 1 < paso ? '✓' : i + 1}</span>
          {t}
        </li>
      ))}
    </ol>
  );
}

/**
 * Precio a mano por UNIDAD (Rodrigo 27-10: «modificar el precio unitario y el sistema calcula la
 * caja»). `precio` y lo que se devuelve son por unidad de la presentación (la caja = c/u × factor).
 */
function PrecioUnitario({
  id,
  factor,
  precio,
  alCambiar,
}: {
  id: string;
  factor: number;
  precio: number | null;
  alCambiar: (precioPresentacion: string) => void;
}) {
  const f = factor > 1 ? factor : 1;
  return (
    <>
      <label htmlFor={id}>Precio c/u</label>
      <CampoPrecio
        id={id}
        valor={precio === null ? '' : String(Math.round(precio / f))}
        alCambiar={(v) => alCambiar(v ? String(Number(v) * f) : '')}
      />
      {f > 1 && precio !== null && (
        <span className="caja-calc">
          × {f} = <b>{pesos(precio)}</b> la caja
        </span>
      )}
    </>
  );
}

/** Precio a mano: mientras se escribe manda el texto (vacío no vuelve a la lista hasta salir). */
function CampoPrecio({
  id,
  valor,
  alCambiar,
}: {
  id: string;
  valor: string;
  alCambiar: (v: string) => void;
}) {
  const [texto, setTexto] = useState<string | null>(null);
  return (
    <input
      id={id}
      type="text"
      inputMode="numeric"
      value={texto ?? valor}
      onFocus={(e) => (setTexto(valor), e.target.select())}
      onChange={(e) => {
        const v = e.target.value.replace(/\D/g, '');
        setTexto(v);
        if (v) alCambiar(v);
      }}
      onBlur={() => {
        if (!texto) alCambiar('');
        setTexto(null);
      }}
    />
  );
}

/** Foto del portal B2B; si no hay, el cuadro queda vacío (no se rompe sin señal). */
function Foto({ sku }: { sku: string }) {
  const [mal, setMal] = useState(false);
  return mal ? (
    <span className="foto" aria-hidden="true" />
  ) : (
    <img className="foto" src={FOTO(sku)} alt="" loading="lazy" onError={() => setMal(true)} />
  );
}

/** − cantidad + (los nombres «Menos»/«Más» los usan las pruebas y los lectores de pantalla). */
function Cantidad({
  nombre,
  valor,
  alCambiar,
}: {
  nombre: string;
  valor: string;
  alCambiar: (v: string) => void;
}) {
  return (
    <div className="cant">
      <button
        type="button"
        aria-label="Menos"
        onClick={() => alCambiar(String(Math.max(0, Number(valor || 0) - 1)))}
      >
        −
      </button>
      <input
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        aria-label={`Cantidad ${nombre}`}
        value={valor}
        placeholder="0"
        onFocus={(e) => e.target.select()}
        onChange={(e) => alCambiar(e.target.value)}
      />
      <button
        type="button"
        aria-label="Más"
        onClick={() => alCambiar(String(Number(valor || 0) + 1))}
      >
        +
      </button>
    </div>
  );
}
